<template>
  <div :key="switch1">
    <loading-component :value="loading" />

    <div class="page-wrap">
      <div class="responsive-toolbar">
        <div class="toolbar-top">
          <div class="toolbar-title">Riders Transfers</div>
          <div v-if="isCompanyCenter" class="gift-balance-label">
            Gift budget: <span class="gift-balance-value">{{ giftBalance | money }}</span>
          </div>
        </div>

        <div class="toolbar-controls">
          <choose-date class="control-field" label="From" v-model="fromDate"></choose-date>
          <choose-date class="control-field" label="To" v-model="toDate"></choose-date>
          <v-btn color="primary" dark class="mb-3 cbtn control-btn" @click="getAccountingData(false)"
            :loading="loading">Get Data</v-btn>
          <v-btn color="primary" dark class="mb-3 cbtn control-btn" @click="getAccountingData(true)"
            :loading="loading">Export CSV</v-btn>
          <v-btn color="primary" dark class="mb-3 cbtn control-btn" @click="payPopup()"
            :loading="loading">Transfer</v-btn>

          <v-text-field v-model="search" class="control-field control-search" append-icon="search" label="Search"
            single-line hide-details @keyup.enter="getAccountingData(false)"
            @click:append="getAccountingData(false)"></v-text-field>
        </div>
      </div>

      <div class="table-wrap">
        <v-data-table :headers="headers" :items="accountingData" class="elevation-1" :pagination.sync="pagination"
          :rows-per-page-items="[5, 10, 15, 25, 100]" :loading="loading" :total-items="totalItems"
          :hide-headers="$vuetify.breakpoint.xsOnly">
          <template v-slot:items="props">
            <tr v-if="!$vuetify.breakpoint.xsOnly">
              <td class="text-xs-left">{{ props.item.id }}</td>
              <td class="text-xs-left">{{ props.item.recipient_name }}</td>
              <td class="text-xs-left">{{ props.item.recipient_reference }}</td>
              <td class="text-xs-left">{{ props.item.required_to_pay | money }}</td>
              <td class="text-xs-left">{{ formatDate(props.item.created_at) }}</td>
            </tr>
            <tr v-else class="mobile-card-row">
              <td :colspan="headers.length" class="mobile-card-cell">
                <div class="payment-card">
                  <div class="card-row">
                    <span class="card-label">#</span>
                    <span class="card-value">{{ props.item.id }}</span>
                  </div>
                  <div class="card-row">
                    <span class="card-label">Rider Name</span>
                    <span class="card-value">{{ props.item.recipient_name }}</span>
                  </div>
                  <div class="card-row">
                    <span class="card-label">Rider Code</span>
                    <span class="card-value">{{ props.item.recipient_reference }}</span>
                  </div>
                  <div class="card-row">
                    <span class="card-label">Amount</span>
                    <span class="card-value">{{ props.item.required_to_pay | money }}</span>
                  </div>
                  <div class="card-row">
                    <span class="card-label">Date</span>
                    <span class="card-value">{{ formatDate(props.item.created_at) }}</span>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </v-data-table>
      </div>
    </div>

    <v-dialog v-model="dialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline"> Top up a rider wallet </span>
        </v-card-title>
        <v-card-text>
          <v-form :key="formKey" ref="transferForm">
            <v-autocomplete :rules="[rules.required]" v-model="returnObject" clearable :items="results"
              :loading="lookupLoading" :search-input.sync="lookupSearch" label="Rider code" item-text="text"
              item-value="text" placeholder="Type the rider code" no-filter return-object>
              <template slot="item" slot-scope="results">
                <v-list-tile-content>
                  <v-list-tile-title v-html="results.item.full_name"></v-list-tile-title>
                  <v-list-tile-sub-title v-html="results.item.public_rider_code"></v-list-tile-sub-title>
                </v-list-tile-content>
              </template>
            </v-autocomplete>

            <p class="lookup-hint">The rider finds this code in his app under "my rider code" — he can also show its QR.</p>

            <center-gift-channel v-if="isCompanyCenter" v-model="channel" :gift-balance="giftBalance" />
            <v-text-field :rules="[rules.required, rules.number]" label="Amount" type="number" v-model="paidAmount"
              placeholder="Amount" />
          </v-form>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-btn flat @click="dialog = false">Cancel</v-btn>
          <v-btn color="primary" class="dialog-btn" :loading="lookupLoading" flat @click="pay()">Add Balance</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


  </div>
</template>

<script>
import { companyNow, companyToday, formatCompanyTime } from "@/util/companyTime";
import LoadingComponent from "@/components/LoadingComponent";
import ChooseDate from "@/components/monaco/input/ChooseDate";
import CenterGiftChannel from "@/components/CenterGiftChannel";
import centerGiftChannel from "@/mixins/centerGiftChannel";
import axios from "axios";
let CancelToken = axios.CancelToken;
let cancel;

/**
 * A charge center topping up a RIDER wallet. The rider is identified by his
 * own public code, never by his phone number — the code exists so a shop can
 * find him without his number being exposed.
 */
export default {
  name: "PassengersTransfers",
  title: "Riders Transfers",
  components: {
    LoadingComponent,
    ChooseDate,
    CenterGiftChannel
  },
  mixins: [centerGiftChannel],
  data() {
    return {
      rules: {
        required: (v) => !!v || "This field is required",
        number: (v) => /^\d+(\.\d+)?$/.test(v) || "Must be a positive number"
      },
      formKey: 0,
      accountingData: [],
      loading: false,
      lookupLoading: false,
      returnObject: null,
      paidAmount: 0,
      search: '',
      dialog: false,
      results: [],
      lookupSearch: null,
      fromDate: companyNow().startOf("year").format("YYYY-MM-DD"),
      toDate: companyToday(),
      totalItems: 0,
      headers: [
        { text: '#', value: 'id' },
        { text: 'Rider Name', value: 'recipient_name' },
        { text: 'Rider Code', value: 'recipient_reference' },
        { text: 'Amount', value: 'required_to_pay' },
        { text: 'Date', value: 'created_at' }
      ],
      pagination: {
        sortBy: "id",
        descending: true,
        rowsPerPage: 25,
        page: 1,
      },

      switch1: null,
      isInitialLoad: true,
    };
  },
  mounted() {
    this.getAccountingData();
    this.$nextTick(() => {
      this.isInitialLoad = false;
    });
  },

  watch: {
    pagination: {
      handler() {
        if (!this.isInitialLoad) {
          this.getAccountingData();
        }
      },
      deep: true,
    },

    lookupSearch(val) {
      // The rider reads his own code off his profile (or shows its QR) and the
      // operator types it; the name comes back so it can be confirmed out loud
      // before any money moves. Codes are 8 characters — wait for a plausible
      // length rather than querying on every keystroke.
      const code = (val || "").replace(/[^A-Za-z0-9]/g, "");
      if (code.length < 6) {
        return;
      }

      this.lookupLoading = true;
      if (cancel) cancel();
      const params = new URLSearchParams();
      params.append("code", code);
      this.axios
        .get("passenger/search", {
          params: params,
          cancelToken: new CancelToken(function executor(c) {
            cancel = c;
          }),
        })
        .then((res) => {
          const rider = res.data.data;
          this.results = rider
            ? [{ text: `${rider.full_name || ""} ${rider.public_rider_code || ""}`, ...rider }]
            : [];
          if (!rider) {
            window.getApp.$emit("SHOW_TOAST", "No rider has that code", "red");
          }
        })
        .catch((err) => console.log(err))
        .finally(() => (this.lookupLoading = false));
    },

    dialog: function (val) {
      this.lookupLoading = false;
      if (!val) {
        this.returnObject = null;
        this.results = [];
      } else {
        this.formKey++;
      }

      this.paidAmount = 0;
    },
  },
  methods: {
    payPopup() {
      this.dialog = true;
    },
    formatDate(date) {
      if (!date) return "";
      // Final-version doc 2026-10-05: Damascus time, whatever the PC zone.
      return formatCompanyTime(date, "DD-MM-YYYY HH:mm:ss", "");
    },

    getAccountingData(excel = false) {
      // Only the export button asks for a file; a search, a page change or a
      // sort must never download one (the handlers used to hand their DOM
      // event in as this flag).
      excel = excel === true;

      const params = new URLSearchParams();
      const sortBy = this.pagination.sortBy || "id";
      const descending = Boolean(this.pagination.descending);

      if (!this.fromDate || !this.toDate) {
        window.getApp.$emit("SHOW_TOAST", "Please select a date range", "red");
        return;
      }
      if (this.fromDate > this.toDate) {
        window.getApp.$emit("SHOW_TOAST", "From date must be before to date", "red");
        return;
      }
      this.loading = true;
      params.append("perPage", String(this.pagination.rowsPerPage));
      params.append("page", this.pagination.page);
      params.append("sortBy", sortBy);
      params.append("descending", String(descending));
      params.append("excel", excel ? 1 : 0);
      params.append("from", this.fromDate);
      params.append("to", this.toDate);
      params.append("recipient_type", "passenger");
      if (this.search) {
        params.append("search", this.search);
      }

      this.axios
        .get("accounting", { params: params, responseType: excel ? "blob" : "json" })
        .then(({ data }) => {
          if (!excel) {
            const responseData = data.data || {};
            this.accountingData = responseData.data || [];
            this.totalItems = responseData.total || 0;
            if (responseData.per_page) {
              this.pagination.rowsPerPage = Number(responseData.per_page);
            }
          } else {
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "riders_transfers.csv");
            document.body.appendChild(link);
            link.click();
          }
        })
        .catch((err) => {
          this.accountingData = [];
          window.getApp.$emit(
            "SHOW_TOAST",
            (err.response && err.response.data && err.response.data.message) || "Could not load the data",
            "red"
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },

    pay() {
      const form = this.$refs.transferForm;
      if (!form.validate()) {
        return;
      }
      this.lookupLoading = true;
      this.axios
        .post("passenger-transfer", {
          public_rider_code: this.returnObject.public_rider_code,
          // Operator types in new pounds; backend stores in old units -> x100.
          amount: this.toStoredAmount(this.paidAmount),
          ...this.channelPayload()
        })
        .then(({ data }) => {
          this.afterTransfer(data && data.data);
          window.getApp.$emit("SHOW_TOAST", "Transfer added successfully", "green");
          this.getAccountingData();
        })
        .catch((err) => {
          window.getApp.$emit(
            "SHOW_TOAST",
            (err.response && err.response.data && err.response.data.message) || "Error Occurrd",
            "red"
          );
        })
        .finally(() => {
          this.dialog = false;
          this.lookupLoading = false;
        });
    }
  },
};
</script>

<style scoped>
.page-wrap {
  width: 100%;
  box-sizing: border-box;
}

.responsive-toolbar {
  background: #fff;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toolbar-top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
}

.toolbar-title {
  font-size: 20px;
  font-weight: 500;
  color: #333;
}

.toolbar-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
}

.control-field {
  flex: 1 1 200px;
  min-width: 160px;
  max-width: 240px;
}

.control-search {
  flex: 0 1 280px;
  max-width: 280px;
  margin-left: auto !important;
}

.control-btn {
  flex: 0 0 auto;
}

.table-wrap {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.mobile-card-cell {
  padding: 12px !important;
  height: auto !important;
}

.mobile-card-row td {
  border-bottom: 8px solid #f5f5f5 !important;
}

.payment-card {
  width: 100%;
  background: #fff;
  border-radius: 6px;
  padding: 4px 8px;
}

.dialog-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.dialog-btn {
  flex: 1 1 45%;
  min-width: 0;
  margin: 0 !important;
}

@media (max-width: 600px) {
  .responsive-toolbar {
    padding: 10px 12px;
  }
  .toolbar-title {
    font-size: 18px;
  }
  .control-field,
  .control-search {
    flex: 1 1 100%;
    max-width: 100%;
    min-width: 0;
    margin-left: 0 !important;
  }
  .control-btn {
    flex: 1 1 100%;
    width: 100%;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  .dialog-btn {
    flex: 1 1 100%;
  }
}

.filters-container {
  padding: 16px;
}

.cards-container {
  padding: 16px;
}

.card-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.card-row:last-child {
  border-bottom: none;
}

.card-label {
  font-weight: 600;
  color: #666;
  font-size: 14px;
}

.card-value {
  color: #333;
  font-size: 14px;
  text-align: right;
}

.card-value.balance {
  font-weight: 700;
  font-size: 16px;
}

.card-value.balance.positive {
  color: #4caf50;
}

.card-value.balance.negative {
  color: #f44336;
}

.card-actions {
  padding: 8px 16px;
  justify-content: flex-end;
}

.pagination-container {
  padding: 16px;
  display: flex;
  justify-content: center;
}

.date-filter {
  max-width: 170px;
  margin-right: 12px;
}
</style>

<style scoped>
.lookup-hint {
  margin: 4px 0 0;
  font-size: 12px;
  color: #78909c;
}
/* Accountant doc 2026-10-03 item 1.9: the company center's gift budget. */
.gift-balance-label {
  font-size: 14px;
  color: #546e7a;
}

.gift-balance-value {
  font-weight: 600;
  color: #333;
}
</style>
